import { Crown, Sparkles, ArrowRight, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/lib/i18n';

interface PremiumPromptProps {
  isFeatured: boolean;
  accessLevel: 'guest' | 'free' | 'premium';
  reason: string;
  onLogin?: () => void;
  onUpgrade?: () => void;
}

export function PremiumPrompt({ 
  isFeatured, 
  accessLevel, 
  reason,
  onLogin,
  onUpgrade
}: PremiumPromptProps) {
  const { t } = useI18n();

  // For guest users (not logged in)
  if (accessLevel === 'guest') {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
        <div className="bg-foreground/5 rounded-full p-3 mb-4">
          <LogIn className="h-6 w-6 text-foreground" aria-hidden="true" />
        </div>
        <h3 className="text-lg font-medium mb-2">{t('premiumPrompt.guest.title')}</h3>
        <p className="text-sm text-muted-foreground mb-4 max-w-sm">
          {t('premiumPrompt.guest.description')}
        </p>
        {onLogin && (
          <Button onClick={onLogin} className="flex items-center gap-2">
            {t('common.signIn')}
            <ArrowRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  }

  // For free users who need premium access
  if (accessLevel === 'free' && !isFeatured) {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
        <div className="bg-amber-500/10 rounded-full p-4 mb-4">
          <Crown className="h-6 w-6 text-amber-600" />
        </div>
        <h3 className="text-lg font-medium mb-2">{t('premiumPrompt.free.title')}</h3>
        <p className="text-sm text-muted-foreground mb-4 max-w-sm">
          {t('premiumPrompt.free.description')}
        </p>
        <div className="flex flex-col gap-2 w-full max-w-sm">
          {onUpgrade && (
            <Button onClick={onUpgrade} className="bg-amber-600 hover:bg-amber-700 flex items-center gap-2">
              <Crown className="h-4 w-4" />
              {t('user.actions.upgrade')}
            </Button>
          )}
          <p className="text-xs text-muted-foreground">
            {t('premiumPrompt.free.secondary')}
          </p>
        </div>
      </div>
    );
  }

  // For featured episodes accessed by free users
  if (accessLevel === 'free' && isFeatured) {
    return (
      <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-green-600" />
          <h3 className="font-semibold text-green-800">{t('premiumPrompt.featured.title')}</h3>
        </div>
        <p className="text-sm text-gray-600 mt-1">
          {t('premiumPrompt.featured.description')}
        </p>
        {onUpgrade && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onUpgrade}
            className="mt-3 border-green-300 text-green-700 hover:bg-green-50"
          >
            {t('premiumPrompt.featured.cta')}
          </Button>
        )}
      </div>
    );
  }

  // Fallback for unknown states
  return (
    <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
      <p className="text-sm text-muted-foreground">{reason || t('premiumPrompt.fallback')}</p>
    </div>
  );
}
